import React from 'react'
import { Card } from '@tremor/react'
import { cn } from '@/lib/utils'

interface SkeletonPulseProps {
  className?: string
}

function SkeletonPulse({ className }: SkeletonPulseProps) {
  return (
    <div className={cn(
      "animate-pulse bg-gradient-to-r from-zinc-200 via-zinc-100 to-zinc-200 bg-[length:200%_100%] rounded",
      className
    )} />
  )
}

function CardSkeleton() {
  return (
    <Card className="border-0 shadow-industrial border-l-4 border-l-zinc-200">
      <div className="space-y-3">
        <SkeletonPulse className="h-4 w-24" />
        <SkeletonPulse className="h-10 w-32" />
        <SkeletonPulse className="h-2 w-full mt-4" />
        <div className="flex justify-between mt-4">
          <SkeletonPulse className="h-3 w-20" />
          <SkeletonPulse className="h-6 w-16 rounded-md" />
        </div>
      </div>
    </Card>
  )
}

function HeroSkeleton() {
  return (
    <Card className="border-0 bg-zinc-900">
      <div className="space-y-4">
        <SkeletonPulse className="h-4 w-32 bg-zinc-700" />
        <SkeletonPulse className="h-12 w-40 bg-zinc-700" />
        <SkeletonPulse className="h-4 w-full bg-zinc-700 mt-6" />
        <div className="grid grid-cols-3 gap-4 mt-4">
          <SkeletonPulse className="h-16 bg-zinc-800 rounded-lg" />
          <SkeletonPulse className="h-16 bg-zinc-800 rounded-lg" />
          <SkeletonPulse className="h-16 bg-zinc-800 rounded-lg" />
        </div>
      </div>
    </Card>
  )
}

function LeaderboardSkeleton() {
  return (
    <Card className="border-0 shadow-industrial p-0 overflow-hidden">
      <div className="px-6 py-4 border-b border-zinc-100">
        <div className="flex items-center gap-3">
          <SkeletonPulse className="h-9 w-9 rounded-lg" />
          <SkeletonPulse className="h-5 w-28" />
        </div>
      </div>
      <div className="divide-y divide-zinc-50">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="px-6 py-4 flex items-center gap-4">
            <SkeletonPulse className="h-8 w-8 rounded-full" />
            <SkeletonPulse className="h-10 w-10 rounded-full" />
            <SkeletonPulse className="h-4 flex-1" />
            <SkeletonPulse className="h-6 w-12" />
          </div>
        ))}
      </div>
    </Card>
  )
}

function TableSkeleton() {
  return (
    <Card className="border-0 shadow-industrial p-0 overflow-hidden">
      <div className="px-6 py-4 border-b border-zinc-100 bg-zinc-50">
        <div className="flex items-center gap-3">
          <SkeletonPulse className="h-9 w-9 rounded-lg" />
          <SkeletonPulse className="h-5 w-48" />
        </div>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-4">
              <SkeletonPulse className="h-9 w-9 rounded-full" />
              <SkeletonPulse className="h-4 w-24" />
              <SkeletonPulse className="h-4 flex-1" />
              <SkeletonPulse className="h-6 w-16 rounded-md" />
              <SkeletonPulse className="h-6 w-16 rounded-md" />
            </div>
          ))}
        </div>
      </div>
    </Card>
  )
}

function ActivitySkeleton() {
  return (
    <Card className="border-0 shadow-industrial p-0 overflow-hidden">
      <div className="px-6 py-4 border-b border-zinc-100">
        <div className="flex items-center gap-3">
          <SkeletonPulse className="h-9 w-9 rounded-lg" />
          <SkeletonPulse className="h-5 w-28" />
        </div>
      </div>
      <div className="px-6 py-4 space-y-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex gap-4">
            <SkeletonPulse className="h-2.5 w-2.5 rounded-full flex-shrink-0 mt-1" />
            <div className="flex-1 space-y-2">
              <SkeletonPulse className="h-4 w-full" />
              <SkeletonPulse className="h-3 w-16" />
            </div>
          </div>
        ))}
      </div>
    </Card>
  )
}

export function DashboardSkeleton() {
  return (
    <div className="space-y-8 animate-fade-in">
      {/* Period Toggle */}
      <SkeletonPulse className="h-10 w-56 rounded-lg" />

      {/* Hero Section */}
      <HeroSkeleton />

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <CardSkeleton />
        <CardSkeleton />
        <CardSkeleton />
        <CardSkeleton />
      </div>

      {/* Leaderboards & Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <LeaderboardSkeleton />
        <LeaderboardSkeleton />
        <ActivitySkeleton />
      </div>

      {/* Table */}
      <TableSkeleton />
    </div>
  )
}
